import { useEffect } from "react";
import {
  comboLabel,
  PDV_SHORTCUT_CATEGORY_LABEL,
  PDV_SHORTCUT_META,
  type PdvShortcutAction,
  type PdvShortcutCategory,
} from "@vertex/validators/pdv-pure";
import { Kbd } from "@vertex/ui/primitives/kbd";
import { useShortcutRegistry } from "./registry";

/**
 * Overlay do F1. Mostra **só as ações habilitadas no escopo atual** — listar
 * "F7 remover item" enquanto o pagamento está aberto ensinaria uma tecla que
 * não responde, e o operador conclui que o sistema está travado.
 *
 * Camada própria em vez do `Dialog`: a ajuda precisa abrir por cima de
 * qualquer coisa, inclusive de um diálogo, e o motor de animação do `Dialog` não
 * se paga numa tela que é lida e fechada em 3 segundos.
 */
export function ShortcutsHelp({ onClose }: { onClose: () => void }) {
  const { combos, acoesAtivas } = useShortcutRegistry();

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape" || e.code === "F1") {
        e.preventDefault();
        onClose();
      }
    }
    window.addEventListener("keydown", onKey, { capture: true });
    return () => window.removeEventListener("keydown", onKey, { capture: true });
  }, [onClose]);

  const ativas = acoesAtivas();
  const porCategoria = new Map<PdvShortcutCategory, PdvShortcutAction[]>();
  for (const a of ativas) {
    const cat = PDV_SHORTCUT_META[a].category;
    porCategoria.set(cat, [...(porCategoria.get(cat) ?? []), a]);
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Atalhos do teclado"
      className="fixed inset-0 z-50 flex items-center justify-center p-6"
      style={{ background: "var(--overlay)" }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="max-h-full w-full max-w-[620px] overflow-auto rounded-xl p-6"
        style={{ background: "var(--panel)", border: "1px solid var(--line)", boxShadow: "var(--shadow-lg)" }}
      >
        <h2 className="mb-1 font-semibold" style={{ fontSize: "var(--fs-h3)" }}>
          Atalhos
        </h2>
        <p className="mb-5" style={{ fontSize: "var(--fs-meta)", color: "var(--ink-3)" }}>
          Só o que responde nesta tela. Pressione Esc ou F1 para fechar.
        </p>

        {[...porCategoria.entries()].map(([cat, acoes]) => (
          <section key={cat} className="mb-5 last:mb-0">
            <h3
              className="mb-2 uppercase"
              style={{ fontSize: "var(--fs-label)", color: "var(--ink-3)", letterSpacing: "0.08em" }}
            >
              {PDV_SHORTCUT_CATEGORY_LABEL[cat]}
            </h3>
            <dl className="grid gap-1.5">
              {acoes.map((a) => (
                <div key={a} className="flex items-center justify-between gap-4">
                  <dt style={{ fontSize: "var(--fs-meta)" }}>{PDV_SHORTCUT_META[a].label}</dt>
                  <dd className="flex shrink-0 gap-1.5">
                    {combos(a).map((c) => (
                      <Kbd key={c}>{comboLabel(c).join(" + ")}</Kbd>
                    ))}
                  </dd>
                </div>
              ))}
            </dl>
          </section>
        ))}

        {ativas.length === 0 ? (
          <p style={{ fontSize: "var(--fs-meta)", color: "var(--ink-3)" }}>
            Nenhum atalho responde nesta tela.
          </p>
        ) : null}
      </div>
    </div>
  );
}
